import { useState } from 'react';

import moment from 'moment';
import { useParams } from 'react-router-dom';

import { CommentSection } from './comments-section';
import { FactDocumentDetails } from './document-details';
import { RelevanceSelector } from './relevance-selector';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { ProcessingChip, ProcessingState } from '../processing-chip';
import { Input } from '../shadcn/input';
import { Popover, PopoverContent, PopoverTrigger } from '../shadcn/popover';
import { Skeleton } from '../shadcn/skeleton';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteChronologyFactWithSync } from '@/api/mutations/useDeleteChronologyFactWithSync';
import useUpdateChronologyFactWithSync from '@/api/mutations/useUpdateChronologyFactWithSync';
import { useVerifyChronologyEvent } from '@/api/mutations/useVerifyChronologyFact';
import { Chronology } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { relevanceIcons, relevanceMapping } from '@/helpers/filterNames';
import { PermissionGate } from '@/hooks/useHasPermission';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { Chip } from '@/new/components/chip';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { EditFieldDialog } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';
import { ChronologyFact, Relevance } from '@/types';

interface FactDropdownProps {
  fact: ChronologyFact;
  onViewDocument?: (docId: string, factId: string, pageNumber: number) => void;
  isSelected?: boolean;
  subsetId: string;
  chronology: Chronology;
}

export const ChronologyFactDropdown = ({
  fact: chronologyFact,
  onViewDocument,
  isSelected,
  subsetId,
  chronology,
}: FactDropdownProps) => {
  const [relevance, setRelevance] = useState(chronologyFact.relevance);
  const { matterId, chronologyId } = useParams();
  const { openDocViewer } = useDocumentNavigation();
  const { user } = useUserContext();
  const isCreator = chronology?.createdById === user?.id;

  if (!matterId || !chronologyId) return null;

  const [factDateText, setFactDateText] = useState(chronologyFact.factDateText ?? '');
  const [isFactDateTextPopoverOpen, setIsFactDateTextPopoverOpen] = useState(false);

  const { mutate: updateFact, isLoading: isUpdatingFact } = useUpdateChronologyFactWithSync({ matterId, subsetId });

  const handleUpdateRelevance = (newRelevance: Relevance) => {
    setRelevance(newRelevance);
    updateFact({ field: 'relevance', value: newRelevance, factId: chronologyFact.id, chronologyId });
  };

  const handleUpdateSignificance = (newSignificance: string) => {
    updateFact({ field: 'significance', value: newSignificance, factId: chronologyFact.id, chronologyId });
  };

  const handleUpdateDateText = (newDateText: string) => {
    trackCustomEvent(TrackingEventsV2.ChronologyUpdateFactDate, { chronologyId, subsetId });
    updateFact({ field: 'factDateText', value: newDateText, factId: chronologyFact.id, chronologyId });
  };

  const handleUpdateDescription = (newDescription: string) => {
    updateFact({ field: 'description', value: newDescription, factId: chronologyFact.id, chronologyId });
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { mutateAsync: deleteChronologyFact, isLoading: isDeletingFact } = useDeleteChronologyFactWithSync(
    matterId,
    subsetId,
    chronologyId,
  );

  const handleDeleteFact = async () => {
    trackCustomEvent(TrackingEventsV2.ChronologyRemoveFact, { chronologyId, subsetId });

    try {
      await deleteChronologyFact(chronologyFact.id);
    } catch (err) {
      console.error('Fetch Error: ', err);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const [verified, setVerified] = useState(chronologyFact.verifiedById !== null);
  const { mutate: updateVerifiedMutation } = useVerifyChronologyEvent(matterId, subsetId);

  const handleToggleVerify = async (verify: boolean) => {
    setVerified(verify);
    updateVerifiedMutation({ factId: chronologyFact.id, verified: verify });
  };

  return (
    <>
      <AccordionItem
        value={chronologyFact.id}
        className={cn(
          'group w-full rounded-lg border border-gray-200 px-4.5 transition-opacity duration-150',
          isSelected ? 'z-10 border-teal-300' : '',
          isUpdatingFact ? 'opacity-50' : 'opacity-100',
        )}
      >
        <AccordionTrigger className="w-full py-3">
          <div className="mr-6 w-full font-normal text-gray-800">
            <div className="mb-1.5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <p className={cn(isSelected && 'text-teal-500', 'flex items-center gap-2 font-medium break-all')}>
                  <Icon className={cn(isSelected ? 'text-teal-500' : 'text-gray-500', 'lucide--calendar')} />
                  <span className="-mb-0.25 line-clamp-1">{chronologyFact.factDateText ?? '(Date Unknown)'}</span>

                  <PermissionGate permission={Permission.ChronologyEdit} orAllowed={isCreator} uiMode="hidden">
                    <Popover
                      open={isFactDateTextPopoverOpen}
                      onOpenChange={(isOpen) => {
                        setIsFactDateTextPopoverOpen(isOpen);
                        // reset input field when popover opens
                        if (isOpen) {
                          setFactDateText(chronologyFact.factDateText ?? '');
                        }
                      }}
                    >
                      <PopoverTrigger onClick={(e) => e.stopPropagation()}>
                        <Icon className="lucide--pencil text-xs text-gray-400 hover:text-gray-800" />
                      </PopoverTrigger>
                      <PopoverContent className="max-w-96" side="right">
                        <p className="text-gray-600">
                          Enter date in any format e.g. 2001, 9th June 2003, 2006-2007, September 2009
                        </p>
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleUpdateDateText(factDateText);
                            setIsFactDateTextPopoverOpen(false);
                          }}
                          className="mt-3 mb-1 flex gap-1.5"
                        >
                          <Input
                            value={factDateText}
                            onChange={(e) => {
                              setFactDateText(e.target.value);
                            }}
                          />
                          <Button type="submit" disabled={factDateText === (chronologyFact.factDateText ?? '')}>
                            <Icon className="lucide--arrow-right" />
                          </Button>
                        </form>
                      </PopoverContent>
                    </Popover>
                  </PermissionGate>
                </p>
              </div>

              <div className="flex items-center justify-end gap-2">
                {relevance ? (
                  <Chip className={cn(CHIP_COLOURS.relevance[relevance], 'border')}>
                    <Icon className={relevanceIcons[relevance]} />
                    {relevanceMapping[relevance]}
                  </Chip>
                ) : (
                  <ProcessingChip processingState={ProcessingState.Processing} processingText="Processing Relevance" />
                )}

                {chronologyFact.issues && chronologyFact.issues.length > 0 && (
                  <Popover>
                    <PopoverTrigger onClick={(e) => e.stopPropagation()}>
                      <Chip
                        className={cn(
                          CHIP_COLOURS.issue,
                          'border duration-150 hover:border-blue-400 hover:bg-blue-100/80 hover:text-blue-700',
                        )}
                      >
                        {chronologyFact.issues.length} {chronologyFact.issues.length === 1 ? 'Issue' : 'Issues'}
                      </Chip>
                    </PopoverTrigger>
                    <PopoverContent side="left" className="w-fit max-w-md">
                      <p className="mb-3 flex items-center gap-1.5 text-gray-600">
                        <Icon className="lucide--group" />
                        Issues
                      </p>
                      <div className="flex w-full flex-wrap gap-1.5">
                        {chronologyFact.issues.map((i) => (
                          <Chip key={i} className={cn(CHIP_COLOURS.issue)}>
                            {i}
                          </Chip>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {chronologyFact.fact.locations.map((location) => (
                  <div key={location.docId} className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p
                          onClick={(e) => {
                            e.stopPropagation();
                            trackCustomEvent(TrackingEventsV2.DocViewerOpenFromFact);

                            (onViewDocument ?? openDocViewer)(
                              location.docId,
                              chronologyFact.fact.id,
                              location.boundingBoxes?.[0]?.pageNumber ?? 0,
                            );
                          }}
                          className="flex max-w-28 cursor-pointer items-center gap-1.25 text-xs break-all text-ellipsis text-blue-600 duration-150 hover:text-blue-700"
                        >
                          <Icon className="lucide--file mb-0.25" />
                          <span className="line-clamp-1">{location.doc?.file.name}</span>
                        </p>
                      </TooltipTrigger>

                      <TooltipContent>{location.doc?.file.name}</TooltipContent>
                    </Tooltip>

                    <Chip className={CHIP_COLOURS.type}>{location.doc?.type}</Chip>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-start justify-between gap-2">
              <p className={`${isSelected ? 'text-gray-900' : 'text-gray-800'} group-data-[state=closed]:line-clamp-1`}>
                {chronologyFact.description}

                <PermissionGate permission={Permission.ChronologyEdit} orAllowed={isCreator} uiMode="hidden">
                  <span onClick={(e) => e.stopPropagation()}>
                    <EditFieldDialog
                      title="Edit Fact Description"
                      description="Update the description for this fact."
                      fieldLabel="Fact Description"
                      fieldType="textarea"
                      currentValue={chronologyFact.description ?? ''}
                      onSave={handleUpdateDescription}
                      triggerClassName="group-data-[state=open]:animate-in group-data-[state=open]:fade-in group-data-[state=closed]:hidden ml-1.5 p-0.5 text-xs"
                    />
                  </span>
                </PermissionGate>
              </p>

              <PermissionGate permission={Permission.ChronologyEdit} orAllowed={isCreator} uiMode="disabled">
                {verified === true ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="flex"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleVerify(false);
                        }}
                      >
                        <Chip
                          className={cn(
                            CHIP_COLOURS.verified,
                            'mr-0.5 border transition-colors hover:border-green-200 hover:bg-green-50/50',
                          )}
                        >
                          <Icon className="lucide--badge-check" />
                          <span>Verified</span>
                        </Chip>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <span className="text-gray-600">
                        Verified by <strong>{chronologyFact.verifiedBy?.email}</strong> on{' '}
                        {moment(chronologyFact.verifiedDate).format('MMM DD, YYYY')}.
                      </span>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="flex"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleVerify(true);
                        }}
                      >
                        <Chip
                          className={cn(
                            'border-gray-300 bg-gray-50 text-gray-700',
                            'mr-0.5 border transition-colors hover:border-green-300 hover:bg-green-50 hover:text-green-600',
                          )}
                        >
                          <span>Verify</span>
                        </Chip>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">Verify Fact in this Chronology</TooltipContent>
                  </Tooltip>
                )}
              </PermissionGate>
            </div>
          </div>
        </AccordionTrigger>

        <AccordionContent className="flex flex-col gap-4 pb-5">
          <FactDocumentDetails
            fact={chronologyFact}
            gotoDocReference={
              onViewDocument
                ? onViewDocument
                : (docId: string, factId: string, pageNum: number) => {
                    openDocViewer(docId, factId, pageNum);
                    trackCustomEvent(TrackingEventsV2.DocViewerOpenFromFact);
                  }
            }
          />

          <div className="relative mt-1.5 flex gap-6 rounded-md border bg-gray-50 px-4 py-3 pt-9 text-xs">
            {/* chronology indicator */}
            <div className="absolute -top-[1px] left-3 flex items-center gap-1.5 rounded-b-lg border bg-white px-2 py-1 text-xs font-medium text-gray-700">
              <Icon className="lucide--git-fork" />
              <span>{chronology.title ?? ''}</span>
            </div>

            {/* significance */}
            <div className="flex grow flex-col gap-2">
              <p className="flex items-center gap-2 font-medium">
                <span>Significance</span>

                <TooltipIcon
                  side="right"
                  tooltipContent={`Short analysis of this fact's significance as it pertains to this subset's issues. Changes made here will only affect this chronology.`}
                />

                <PermissionGate permission={Permission.ChronologyEdit} orAllowed={isCreator} uiMode="hidden">
                  <EditFieldDialog
                    title="Edit Fact Significance"
                    description="Update the significance for this fact."
                    fieldLabel="Fact Significance"
                    fieldType="textarea"
                    currentValue={chronologyFact.significance ?? ''}
                    onSave={handleUpdateSignificance}
                    triggerClassName="p-0.5 text-[10px]"
                  />
                </PermissionGate>
              </p>

              {chronologyFact.significance ? (
                <p className="text-gray-700">{chronologyFact.significance}</p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-4 w-9/10 bg-gray-200" />
                  <Skeleton className="h-4 w-2/10 bg-gray-200" />
                </div>
              )}
            </div>

            <div className="flex min-w-54 flex-col gap-2">
              <p className="flex items-center gap-2 pl-0.5 font-medium">
                Relevant
                <TooltipIcon
                  side="right"
                  tooltipContent={`Is this fact relevant? Changes made here will only affect this chronology.`}
                />
              </p>

              <RelevanceSelector relevanceText={relevance} updateRelevance={handleUpdateRelevance} />
            </div>
          </div>

          <div className="relative px-1">
            <CommentSection fact={chronologyFact.fact} />

            <PermissionGate permission={Permission.ChronologyEdit} orAllowed={isCreator} uiMode="disabled">
              <Button className="absolute top-0 right-0" onClick={() => setIsDeleteModalOpen(true)} size="sm">
                <Icon className="lucide--trash" />
                <span>Remove Fact from Chronology</span>
              </Button>
            </PermissionGate>
          </div>
        </AccordionContent>
      </AccordionItem>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onConfirm={handleDeleteFact}
        isLoading={isDeletingFact}
        title="Remove Fact from Chronology"
      >
        <p>Are you sure you want to remove this fact from the chronology?</p>
      </ConfirmationModal>
    </>
  );
};
